// Top 10 eventos por volume (§4.1) — barras horizontais com rótulo
// pt-BR (vem pronto no payload). Cada barra é um link → Consulta
// filtrada por event.
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { NotificationOverviewTopEventType } from "@/types/notification-center"

import {
  buildNotificationSearchHref,
  formatDashboardInteger,
} from "../dashboard-utils"
import { DashboardChartCard } from "./dashboard-chart-card"

interface DashboardTopEventsChartProps {
  topEvents?: NotificationOverviewTopEventType[]
  isLoading: boolean
  hasError: boolean
  onRetry: () => void
  range: { startDate?: string; endDate?: string }
}

export const DashboardTopEventsChart = ({
  topEvents,
  isLoading,
  hasError,
  onRetry,
  range,
}: DashboardTopEventsChartProps) => {
  const { t, i18n } = useTranslation()
  const chartPrefix =
    "pages.notificationCenter.dashboardPage.charts.topEvents"

  const maxTotal = Math.max(
    ...(topEvents ?? []).map((item) => item.total),
    1
  )

  // Paleta categórica: cor ESTÁVEL por evento (hash do nome), não
  // por posição — o mesmo evento mantém a cor entre dias/rankings.
  const EVENT_BAR_COLORS = [
    "#3b82f6", // blue-500
    "#10b981", // emerald-500
    "#f59e0b", // amber-500
    "#8b5cf6", // violet-500
    "#ef4444", // red-500
    "#06b6d4", // cyan-500
    "#84cc16", // lime-500
    "#d946ef", // fuchsia-500
    "#f97316", // orange-500
    "#14b8a6", // teal-500
  ]
  const eventColor = (event: string) => {
    let hash = 0
    for (const char of event)
      hash = (hash * 31 + char.charCodeAt(0)) % 997
    return EVENT_BAR_COLORS[hash % EVENT_BAR_COLORS.length]
  }

  return (
    <DashboardChartCard
      title={t(`${chartPrefix}.title`)}
      testId="dashboard-top-events"
      isLoading={isLoading}
      hasError={hasError}
      isEmpty={!topEvents?.length}
      onRetry={onRetry}
    >
      <ol>
        {(topEvents ?? []).map((item) => (
          <li key={item.event}>
            <Link
              to={buildNotificationSearchHref({
                event: item.event,
                ...range,
              })}
              aria-label={t(`${chartPrefix}.link`, {
                event: item.label,
              })}
              className="group flex items-center gap-2 rounded-md px-2 py-0.5 text-xs hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate group-hover:underline">
                  {item.label}
                </span>
                <span
                  aria-hidden="true"
                  className="mt-0.5 block h-1 rounded-full"
                  style={{
                    width: `${Math.max((item.total / maxTotal) * 100, 2)}%`,
                    backgroundColor: eventColor(item.event),
                  }}
                />
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatDashboardInteger(
                  item.total,
                  i18n.language
                )}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </DashboardChartCard>
  )
}
